import {
  containersSelector,
  EntityIdentifier,
  firstOrUndefined,
  geofencesSelector,
  getEntityIdentifier,
  getRequiredObjectId,
  getRequiredObjectType,
  SiteEntity,
  useGetAllWellsQuery,
  useGetContainersBySiteQuery,
  useGetGeofencesByObjectQuery,
  wellsSelector
} from "@ots-frontend/ots-data";
import {memo, useCallback, useEffect, useMemo, useState} from "react";
import {skipToken} from "@reduxjs/toolkit/query";
import {ActionIcon, Box, Card, Container, Divider, Grid, Group, Modal, Text, Title} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {useApp} from "../../../providers/RegionProvider/RegionProvider";
import {SiteTypeBadge} from "../../badge/SiteTypeBadge/SiteTypeBadge";
import {SiteMap} from "../../map/SiteMap/SiteMap";
import {ContainerDataTable} from "../../data-table/ContainerDataTable/ContainerDataTable";
import {WellDataTable} from "../../data-table/WellDataTable/WellDataTable";
import {IconHierarchy, IconPlus, IconTruck} from "@tabler/icons-react";
import {RestrictionDataTableCard} from "../../card/RestrictionDataTableCard/RestrictionDataTableCard";
import {ObjectMappingDataTableCard} from "../../card/ObjectMappingDataTableCard/ObjectMappingDataTableCard";
import {ObjectMeterDataTableCard} from "../../card/ObjectMeterDataTableCard/ObjectMeterDataTableCard";
import {HasAccess, HasEntityAccess} from "../../auth/HasAccess/HasAccess";
import {ObjectPipelineDataTableCard} from "../../card/ObjectPipelineDataTableCard/ObjectPipelineDataTableCard";
import {DefaultText} from "../../text/DefaultText/DefaultText";
import {EntityAnchor} from "../../anchor/EntityAnchor/EntityAnchor";
import {GeofenceDataTable} from "../../data-table/GeofenceDataTable/GeofenceDataTable";
import {useNavigate} from "react-router-dom";
import {
  RrcLocationProfileDataTableCard
} from "../../card/RrcLocationProfileDataTableCard/RrcLocationProfileDataTableCard";
import {formatAddressOneLine} from "../../detail/AddressSummary/AddressSummary";
import {
  RrcEp9WasteProfileDataTableCard
} from "../../card/RrcEp9WasteProfileDataTableCard/RrcEp9WasteProfileDataTableCard";
import {EntityGraphView} from "../../graph/EntityGraphView/EntityGraphView";
import {ContainerStatusIcon} from "../../icon/ContainerStatusIcon/ContainerStatusIcon";
import {useDisclosure} from "@mantine/hooks";
import {
  AllocatableLoadSettingsDataTableCard
} from "../../card/AllocatableLoadSettingsDataTableCard/AllocatableLoadSettingsDataTableCard";

export type SiteDetailsProps = {
  site: SiteEntity
}

const NameSection = memo((props: { site: SiteEntity }) => {
  const {site} = props

  const {t} = useTranslation("common")

  return (
    <>
      <Text ta="left" fz="sm" weight={500} mt="md">
        {t("profile.site.info.field.name.label", "Name")}
      </Text>
      <Text ta="left" c="dimmed" fz="xs">
        {site.name}
      </Text>
    </>
  )
}, (prev, next) => prev.site.name === next.site.name)

const TypeSection = memo((props: { site: SiteEntity }) => {
  const {site} = props

  const {t} = useTranslation("common")

  return (
    <>
      <Text ta="left" fz="sm" weight={500} mt="md">
        {t("profile.site.info.field.type.label", "Type")}
      </Text>
      {site.type && <SiteTypeBadge type={site.type}/>}
    </>
  )
}, (prev, next) => prev.site.type?.code === next.site.type?.code)

const OperatorSection = memo((props: { site: SiteEntity }) => {
  const {site} = props

  const {t} = useTranslation("common")

  return (
    <>
      <Text ta="left" fz="sm" weight={500} mt="md">
        {t("profile.site.info.field.operator.label", "Operating Company")}
      </Text>
      <Text ta="left" c="dimmed" fz="xs">
        <EntityAnchor
          value={{
            objectId: site.companyId,
            objectType: "COMPANY",
            objectName: site.companyName,
          }}
        />
      </Text>
    </>
  )
}, (prev, next) => prev.site.companyName === next.site.companyName)

const OwnerSection = memo((props: { site: SiteEntity }) => {
  const {site} = props

  const {t} = useTranslation("common")

  return (
    <>
      <Text ta="left" fz="sm" weight={500} mt="md">
        {t("profile.site.info.field.owner.label", "Owning Company")}
      </Text>
      <Text ta="left" c="dimmed" fz="xs">
        {site.ownerName}
      </Text>
    </>
  )
}, (prev, next) => prev.site.ownerName === next.site.ownerName)

const RouteSection = memo((props: { site: SiteEntity }) => {
  const {site} = props

  const {t} = useTranslation("common")

  const route = useMemo(() => {
    return firstOrUndefined(site.routes)
  }, [site.routes])

  const routeName = useMemo(() => {
    let name: string | undefined = undefined

    if (route) {
      name = route.name
      if (route.routeGroupName) {
        name += ` (${route.routeGroupName})`
      }
    }

    return name
  }, [route])

  return (
    <>
      <Text ta="left" fz="sm" weight={500} mt="md">
        {t("profile.site.info.field.route.label", "Route")}
      </Text>
      <Text ta="left" c="dimmed" fz="xs">
        {route &&
          <EntityAnchor
            value={{
              objectId: route.id,
              objectName: routeName ?? route.name,
              objectType: 'ROUTE'
            }}
          />
        }
      </Text>
    </>
  )
}, (prev, next) => prev.site.routes === next.site.routes)

const OperatingAreaSection = memo((props: { site: SiteEntity }) => {
  const {site} = props

  const {t} = useTranslation("common")

  return (
    <>
      <Text ta="left" fz="sm" weight={500} mt="md">
        {t("profile.site.info.field.operatingarea.label", "Operating Area")}
      </Text>
      <Text ta="left" c="dimmed" fz="xs">
        {site.operatingAreaId &&
          <EntityAnchor
            value={{
              objectId: site.operatingAreaId,
              objectName: site.operatingAreaName,
              objectType: 'OPERATINGAREA'
            }}
          />
        }
      </Text>
    </>
  )
}, (prev, next) => prev.site.routes === next.site.routes)

const TransportViaWaterSection = memo((props: { site: SiteEntity }) => {
  const {site} = props

  const {t} = useTranslation("common")

  return (
    <>
      <Text ta="left" fz="sm" weight={500} mt="md">
        {t("profile.site.info.field.transportviapipeline.label", "Transport Water Via")}
      </Text>
      <Text ta="left" c="dimmed" fz="xs">
        <Group spacing="xs">
          {site.transportViaPipeline ?
            <ContainerStatusIcon name="water-pipeline" width={14} height={14}/> :
            <IconTruck size="14"/>}
          {site.transportViaPipeline ? "Pipeline" : "Truck"}
        </Group>
      </Text>
    </>
  )
}, (prev, next) => prev.site.transportViaPipeline === next.site.transportViaPipeline)

const TransportViaOilSection = memo((props: { site: SiteEntity }) => {
  const {site} = props

  const {t} = useTranslation("common")

  return (
    <>
      <Text ta="left" fz="sm" weight={500} mt="md">
        {t("profile.site.info.field.transportoilviapipeline.label", "Transport Oil Via")}
      </Text>
      <Text ta="left" c="dimmed" fz="xs">
        <Group spacing="xs">
          {site.transportOilViaPipeline ? <ContainerStatusIcon name="oil-pipeline" width={14} height={14}/> :
            <IconTruck size="14"/>}
          {site.transportOilViaPipeline ? "Pipeline" : "Truck"}
        </Group>
      </Text>
    </>
  )
}, (prev, next) => prev.site.transportOilViaPipeline === next.site.transportOilViaPipeline)

export const SiteDetails = (props: SiteDetailsProps) => {
  const {site} = props;

  const {regionId, compliance} = useApp();
  const {t} = useTranslation(["translation", "common"]);
  const navigate = useNavigate()

  const [opened, { close, open }] = useDisclosure(false);
  const [showMap, setShowMap] = useState<boolean>(false);

  const queryArg: EntityIdentifier | undefined = useMemo(() => {
    if (site) {
      return {
        id: getRequiredObjectId(site),
        type: getRequiredObjectType(site)
      };
    }

    return undefined;
  }, [site]);

  const selectGeofences = useMemo(() => {
    return geofencesSelector();
  }, []);

  const {geofences, isGeofencesLoading} = useGetGeofencesByObjectQuery(queryArg ?? skipToken, {
    selectFromResult: (result) => ({
      ...result,
      geofences: selectGeofences(result),
      isGeofencesLoading: result.isLoading,
    })
  });

  const selectContainers = useMemo(() => {
    return containersSelector();
  }, []);

  const {containers, isContainersLoading} = useGetContainersBySiteQuery(regionId ? {
    regionId: regionId,
    siteId: site.id,
  } : skipToken, {
    selectFromResult: (result) => ({
      containers: selectContainers(result, {siteId: site.id, showDoNotUse: true}),
      isContainersLoading: result.isLoading
    })
  });

  const selectWells = useMemo(() => {
    return wellsSelector();
  }, []);

  const {wells, isWellsLoading} = useGetAllWellsQuery(regionId ?? skipToken, {
    selectFromResult: (result) => ({
      wells: selectWells(result, {siteId: site.id, showDoNotUse: true}),
      isWellsLoading: result.isLoading
    })
  });

  useEffect(() => {
    setShowMap(typeof site.latLng !== "undefined" || geofences.length > 0);
  }, [site, geofences]);

  const onNewGeofenceClick = useCallback(() => {
    navigate("/geofences/form", {
      state: {
        id: undefined,
        formDefaults: {
          latCoord: site.latCoord,
          longCoord: site.longCoord,
          object: getEntityIdentifier(site)
        }
      }
    });
  }, [navigate, site])

  const onNewContainerClick = useCallback(() => {
    navigate("/containers/form", {
      state: {
        id: undefined,
        formDefaults: {
          site: site.id,
          company: site.companyId,
          freightType: site.freightTypeId,
        }
      }
    });
  }, [navigate, site])

  return (
    <Grid>
      <Grid.Col span={6}>
        <Card shadow="sm" padding="lg" radius="md" withBorder>

          <Group position="apart" pb="sm">
            <Title order={4}>{t("profile.site.info.section.details.label", "Details")}</Title>
            <ActionIcon onClick={() => open()}>
              <IconHierarchy size="1.125rem"/>
            </ActionIcon>
          </Group>

          <Divider/>

          <Grid>
            <Grid.Col span={6}>
              <NameSection site={site}/>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.site.info.field.afe.label", "AFE")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {site.afe &&
                  <>
                    {site.afePrefix && site.afePrefix}
                    <Text fw={700} inherit={true} span={true}>
                      {` ${site.afe} `}
                    </Text>
                    {site.afeSuffix && site.afeSuffix}
                  </>
                }
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <TypeSection site={site}/>
            </Grid.Col>
            <Grid.Col span={12}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.site.info.field.qrcode.label", "QR Code")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {site.barcode}
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.site.info.field.leaseid.label", "Lease ID")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {site.leaseId}
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.site.info.field.leasename.label", "Lease Name")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {site.leaseName}
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <OperatorSection site={site}/>
            </Grid.Col>
            <Grid.Col span={6}>
              <OwnerSection site={site}/>
            </Grid.Col>
            <Grid.Col span={6}>
              <OperatingAreaSection site={site}/>
            </Grid.Col>
            <Grid.Col span={6}>
              <RouteSection site={site}/>
            </Grid.Col>
            <Grid.Col span={6}>
              <TransportViaWaterSection site={site}/>
            </Grid.Col>
            <Grid.Col span={6}>
              <TransportViaOilSection site={site}/>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.site.info.field.usable.label", "Usable")}
              </Text>
              <Text ta="left" c={site.doNotUse ? "warning" : "dimmed"} fz="xs">
                {site.doNotUse ? t("common.shared.no", "No") : t("common.shared.yes", "Yes")}
              </Text>
            </Grid.Col>
          </Grid>
        </Card>
      </Grid.Col>

      <Grid.Col span={6}>
        <Card shadow="sm" padding="lg" radius="md" withBorder>

          <Group position="apart" pb="sm">
            <Title order={4}>{t("profile.site.info.section.location.label", "Location")}</Title>
          </Group>

          <Divider/>

          <Grid>
            {site.address && !site.address.isEmpty &&
              <Grid.Col span={12}>
                <Text ta="left" fz="sm" weight={500} mt="md">
                  {t("profile.site.info.field.address.label", "Address")}
                </Text>
                <Text ta="left" c="dimmed" fz="xs">
                  {formatAddressOneLine(site.address)}
                </Text>
              </Grid.Col>
            }
            <Grid.Col span={4}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.site.info.field.latitude.label", "Latitude")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {site.latCoord}
              </Text>
            </Grid.Col>
            <Grid.Col span={4}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.site.info.field.longitude.label", "Longitude")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {site.longCoord}
              </Text>
            </Grid.Col>
            <Grid.Col span={4}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.site.info.field.geofence.label", "Geofence")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {site.geofenceExtension}
              </Text>
            </Grid.Col>
            {site.geofenceSiteGroupId &&
              <Grid.Col span={4}>
                <Text ta="left" fz="sm" weight={500} mt="xs">
                  {t("profile.site.info.field.geofencesitegroup.label", "Geofence Group")}
                </Text>
                <Text ta="left" c="dimmed" fz="xs">
                  <DefaultText value={site.geofenceSiteGroupName}/>
                </Text>
              </Grid.Col>
            }
          </Grid>

          <Grid.Col span={12}>
            {showMap && (
              <>
                <Divider variant={"dashed"} my="md"/>
                <Box
                  style={{
                    borderRadius: '12px',
                    overflow: 'hidden',
                  }}
                >
                  <SiteMap
                    width="100%"
                    height={300}
                    site={site}
                    geofences={geofences}
                    apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}
                  />
                </Box>
              </>
            )}
          </Grid.Col>

          <Grid.Col span={12}>
            <Divider pb="sm"/>
            <Group spacing={4} position="right" noWrap>
              <HasEntityAccess
                entityDefs={[
                  {
                    entity: site, permissions: ["site_edit"]
                  },
                  {
                    permissions: ["geofence_create"]
                  }
                ]}
              >
                <ActionIcon color="green" onClick={() => onNewGeofenceClick()}>
                  <IconPlus size={16}/>
                </ActionIcon>
              </HasEntityAccess>
            </Group>
            <GeofenceDataTable
              withBorder
              records={geofences}
              fetching={isGeofencesLoading}
            />
          </Grid.Col>
        </Card>
      </Grid.Col>

      <HasAccess permissions={["site_edit"]}>
        <Grid.Col span={12}>
          <AllocatableLoadSettingsDataTableCard entity={site}/>
        </Grid.Col>
      </HasAccess>

      <HasAccess permissions={["restriction_view"]}>
        <Grid.Col span={12}>
          <RestrictionDataTableCard entity={site}/>
        </Grid.Col>
      </HasAccess>

      <Grid.Col span={12}>
        <Card shadow="sm" padding="lg" radius="md" withBorder>

          <Group position="apart" pb="sm">
            <Title order={4}>Containers</Title>
            <Group spacing={4} position="left" noWrap>
              <HasEntityAccess
                entityDefs={[
                  {
                    entity: site, permissions: ["site_edit"]
                  },
                  {
                    permissions: ["container_create"]
                  }
                ]}
              >
                <ActionIcon color="green" onClick={() => onNewContainerClick()}>
                  <IconPlus size={16}/>
                </ActionIcon>
              </HasEntityAccess>
            </Group>
          </Group>

          <Divider pb="sm"/>

          <ContainerDataTable
            withBorder
            records={containers}
            fetching={isContainersLoading}
          />
        </Card>
      </Grid.Col>

      <Grid.Col span={12}>
        <Card shadow="sm" padding="lg" radius="md" withBorder>

          <Group position="apart" pb="sm">
            <Title order={4}>{t("common.entity.well.plural", "Wells")}</Title>
          </Group>

          <Divider pb="sm"/>

          <WellDataTable
            withBorder
            records={wells}
            fetching={isWellsLoading}
          />
        </Card>
      </Grid.Col>
      <HasAccess permissions={["pipeline_view"]}>
        <Grid.Col span={12}>
          <ObjectPipelineDataTableCard entity={site}/>
        </Grid.Col>
      </HasAccess>
      <HasAccess permissions={["meter_view"]}>
        <Grid.Col span={12}>
          <ObjectMeterDataTableCard entity={site}/>
        </Grid.Col>
      </HasAccess>
      <HasAccess permissions={["object_mapping_view"]}>
        <Grid.Col span={12}>
          <ObjectMappingDataTableCard entity={site}/>
        </Grid.Col>
      </HasAccess>

      {compliance?.rrc === true &&
        <>
          <HasAccess permissions={["rrclocationprofile_view"]}>
            <Grid.Col span={12}>
              <RrcLocationProfileDataTableCard entity={site}/>
            </Grid.Col>
          </HasAccess>
          <HasAccess permissions={["rrcep9wasteprofile_view"]}>
            <Grid.Col span={12}>
              <RrcEp9WasteProfileDataTableCard entity={site}/>
            </Grid.Col>
          </HasAccess>
        </>
      }

      {opened &&
        <Modal opened={opened} onClose={close} size="auto" title="Entity Graph">
          <Container h={700} w={800}>
            <EntityGraphView entity={site} />
          </Container>
        </Modal>
      }
    </Grid>
  );
};

SiteDetails.Name = NameSection
SiteDetails.Type = TypeSection
SiteDetails.Operator = OperatorSection
SiteDetails.Owner = OwnerSection
SiteDetails.Route = RouteSection
SiteDetails.TransportViaWater = TransportViaWaterSection
SiteDetails.TransportViaOil = TransportViaOilSection
